// Home — overview del viaje + día actual / próximo
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { Calendar, MapPin, ChevronRight, Plane, Bed, Sparkles, Share2 } from "lucide-react";
import { TRIP } from "../data/trip";
import { formatDate, formatTime, dayIndexForDate, getDayCenter } from "../data/helpers";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";
import AISheet from "../components/AISheet";
import ShareSheet from "../components/ShareSheet";
import WeatherBadge from "../components/WeatherBadge";
import { toast } from "sonner";

function mergeAIItems(trip, entity, items) {
  // Returns a new trip object with the items merged in.
  const next = { ...trip };
  if (entity === "restaurants") {
    next.restaurants = { ...(trip.restaurants || {}) };
    items.forEach((it) => {
      const city = it.city || it.area || "Otros";
      const cityKey = ["Budapest", "Praga", "Tel Aviv", "Madrid"].find((c) => city.toLowerCase().includes(c.toLowerCase())) || city;
      if (!next.restaurants[cityKey]) next.restaurants[cityKey] = [];
      const id = it.id || `r-ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      next.restaurants[cityKey] = [...next.restaurants[cityKey], { ...it, id }];
    });
  } else if (entity === "places") {
    next.places = { ...(trip.places || {}) };
    items.forEach((it) => {
      const id = it.id || `place-ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      next.places[id] = { ...it, id };
    });
  } else if (entity === "flights") {
    next.flights = [...(trip.flights || [])];
    items.forEach((it) => {
      const id = `f${(next.flights.length || 0) + 1}-ai-${Date.now().toString(36).slice(-4)}`;
      next.flights.push({ id, ...it });
    });
  } else if (entity === "hotels") {
    next.hotels = [...(trip.hotels || [])];
    items.forEach((it) => {
      next.hotels.push({ ...it });
    });
  }
  return next;
}

export default function Home() {
  const base = useTripBase();
  const { trip: ctxTrip, tripMeta, saveTripData } = useTrip();
  const trip = ctxTrip || TRIP;
  const [aiOpen, setAiOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const todayIdx = dayIndexForDate();
  const currentDay =
    todayIdx >= 0 ? trip.days[todayIdx] : trip.days[0];
  const upcoming = trip.days.slice(0, 8);

  const handleAIAdd = async (entity, items) => {
    if (!items?.length) return;
    try {
      const next = mergeAIItems(trip, entity, items);
      await saveTripData(next);
      toast.success(`${items.length} ${entity === "restaurants" ? "restaurante(s)" : entity === "places" ? "lugar(es)" : entity === "flights" ? "vuelo(s)" : "hotel(es)"} agregado(s) ✨`);
    } catch (e) {
      toast.error("No se pudo guardar. Inténtalo de nuevo.");
    }
  };

  return (
    <div className="container-app pt-6 pb-28">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
        data-testid="home-header"
      >
        <p className="section-title">Diario de viaje · {trip.meta.travelers}</p>
        <h1 className="text-[40px] leading-[1.05] font-bold mt-2 text-[hsl(var(--ink))]">
          {trip.meta.title}
        </h1>
        <p className="text-sm text-[hsl(var(--muted))] mt-2 flex items-center gap-2">
          <Calendar size={14} /> {trip.meta.dates}
        </p>
      </motion.header>

      {/* Día destacado */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="card-paper overflow-hidden mb-6"
        data-testid="home-current-day"
      >
        {currentDay.hero && (
          <div className="hero-img h-40 sm:h-48">
            <img
              src={currentDay.hero}
              alt={currentDay.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
        <div className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="chip-ink">
              {currentDay.flag} {currentDay.city}
            </span>
            <span className="text-xs text-[hsl(var(--muted))]">
              {formatDate(currentDay.date)} · {currentDay.dayLabel}
            </span>
          </div>
          <h2 className="text-2xl font-bold leading-tight mb-1">{currentDay.title}</h2>
          <p className="text-sm text-[hsl(var(--ink-soft))]">{currentDay.summary}</p>
          {(() => {
            const cc = getDayCenter(currentDay);
            return cc && currentDay.date ? (
              <div className="mt-3">
                <WeatherBadge lat={cc[0]} lng={cc[1]} date={currentDay.date} city={currentDay.city} />
              </div>
            ) : null;
          })()}
          <Link
            to={`${base}/dia/${currentDay.id}`}
            className="mt-4 inline-flex items-center gap-2 text-[hsl(var(--accent))] font-medium text-sm"
            data-testid="home-current-day-cta"
          >
            Ver el día <ChevronRight size={16} />
          </Link>
        </div>
      </motion.section>

      {/* Timeline de próximos días */}
      <section className="mb-6">
        <h3 className="section-title mb-3">Tu itinerario</h3>
        <div className="relative">
          <div className="timeline-line" />
          <ul className="space-y-3">
            {upcoming.map((d, i) => (
              <motion.li
                key={d.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: 0.05 * i }}
                className="relative pl-10"
                data-testid={`home-day-${d.id}`}
              >
                <span
                  className="absolute left-0 top-1.5 flex items-center justify-center w-8 h-8 rounded-full text-base"
                  style={{
                    background: d.israel
                      ? "hsl(220 50% 92%)"
                      : "hsl(var(--bg-card))",
                    border: `2px solid ${
                      d.israel ? "hsl(var(--jewish))" : "hsl(var(--accent))"
                    }`,
                  }}
                  aria-hidden
                >
                  <span style={{ fontSize: 13 }}>{d.flag.split(" ")[0]}</span>
                </span>
                <Link
                  to={`${base}/dia/${d.id}`}
                  className="block card-paper p-3.5 active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">
                        {formatDate(d.date)} · {d.dayLabel}
                      </p>
                      <p className="font-semibold text-[hsl(var(--ink))] truncate mt-0.5">
                        {d.title}
                      </p>
                      <p className="text-xs text-[hsl(var(--muted))] truncate mt-0.5">
                        {d.city}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-[hsl(var(--muted))] shrink-0" />
                  </div>
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <Link to={`${base}/dias`} className="btn-ghost inline-flex items-center gap-2" data-testid="home-all-days">
            Ver todos los días ({trip.days.length}) <ChevronRight size={14} />
          </Link>
        </div>
      </section>

      {/* Accesos rápidos */}
      <section className="grid grid-cols-2 gap-3">
        <Link to={`${base}/logistica`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-flights">
          <Plane size={20} className="text-[hsl(var(--accent))] mb-2" />
          <p className="text-sm font-semibold">Vuelos y hoteles</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Toda tu logística</p>
        </Link>
        <Link to={`${base}/sabores`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-flavors">
          <span className="text-xl block mb-1">🍰</span>
          <p className="text-sm font-semibold">Sabores típicos</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Qué probar y dónde</p>
        </Link>
        <Link to={`${base}/restaurantes`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-restaurants">
          <span className="text-xl block mb-1">🍽️</span>
          <p className="text-sm font-semibold">Restaurantes</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">60+ con rating y precio</p>
        </Link>
        <Link to={`${base}/map`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-map">
          <MapPin size={20} className="text-[hsl(var(--accent))] mb-2" />
          <p className="text-sm font-semibold">Mapa completo</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Todo el viaje</p>
        </Link>
        <Link to={`${base}/tickets`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-tickets">
          <Bed size={20} className="text-[hsl(var(--accent))] mb-2" />
          <p className="text-sm font-semibold">Mis tickets</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Entradas compradas</p>
        </Link>
        <Link to={`${base}/frases`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-phrases">
          <span className="text-xl block mb-1">💬</span>
          <p className="text-sm font-semibold">Frases útiles</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Húngaro · Checo</p>
        </Link>
        <Link to={`${base}/eventos`} className="card-paper p-4 active:scale-[0.98] transition-transform" data-testid="quick-events">
          <span className="text-xl block mb-1">🎟️</span>
          <p className="text-sm font-semibold">Eventos</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Conciertos · noches</p>
        </Link>
        <Link to={`${base}/diario`} className="card-paper p-4 active:scale-[0.98] transition-transform col-span-2" data-testid="quick-journal">
          <span className="text-xl block mb-1">📖</span>
          <p className="text-sm font-semibold">Mi diario</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Anota memorias, anécdotas, fotos mentales</p>
        </Link>
        <Link to={`${base}/tours`} className="card-paper p-4 active:scale-[0.98] transition-transform col-span-2" data-testid="quick-tours">
          <span className="text-xl block mb-1">🚶</span>
          <p className="text-sm font-semibold">Free walking tours</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Guías locales · pay-what-you-want · 9 tours</p>
        </Link>
        <Link to={`${base}/tips`} className="card-paper p-4 active:scale-[0.98] transition-transform col-span-2" data-testid="quick-tips">
          <span className="text-xl block mb-1">💡</span>
          <p className="text-sm font-semibold">Tips prácticos</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Dinero · transporte · costumbres · Budapest y Praga</p>
        </Link>
        <Link to={`${base}/cambio`} className="card-paper p-4 active:scale-[0.98] transition-transform col-span-2" data-testid="quick-currency">
          <span className="text-xl block mb-1">💱</span>
          <p className="text-sm font-semibold">Cambio de moneda</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">USD · EUR · HUF · CZK · ILS · CLP — funciona offline</p>
        </Link>
        <Link to={`${base}/favoritos`} className="card-paper p-4 active:scale-[0.98] transition-transform col-span-2" data-testid="quick-favorites">
          <span className="text-xl block mb-1">❤️</span>
          <p className="text-sm font-semibold">Favoritos</p>
          <p className="text-xs text-[hsl(var(--muted))] mt-1">Restaurantes · sabores · lugares guardados</p>
        </Link>
        <Link to="/viajes" className="card-paper p-4 active:scale-[0.98] transition-transform col-span-2 text-center text-[hsl(var(--muted))]" data-testid="quick-mytrips">
          <span className="text-xs">← Volver a Mis viajes</span>
        </Link>
      </section>

      <p className="text-center text-[10px] text-[hsl(var(--muted))] mt-10 tracking-wider uppercase">
        Hecho con cariño · Buen viaje
      </p>

      {/* AI floating button */}
      <button
        onClick={() => setAiOpen(true)}
        className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-[hsl(var(--accent))] text-white shadow-xl flex items-center justify-center active:scale-90 transition-transform"
        aria-label="Añadir con IA"
        data-testid="ai-fab"
        style={{ boxShadow: "0 8px 24px hsla(var(--accent), 0.4)" }}
      >
        <Sparkles size={22} />
      </button>

      {/* Share floating button */}
      <button
        onClick={() => setShareOpen(true)}
        className="fixed bottom-40 right-4 z-40 w-14 h-14 rounded-full bg-[hsl(var(--ink))] text-white shadow-xl flex items-center justify-center active:scale-90 transition-transform"
        aria-label="Compartir viaje"
        data-testid="share-fab"
        style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}
      >
        <Share2 size={20} />
      </button>

      <AISheet
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onAdd={handleAIAdd}
        tripContext={`Viaje: ${trip.meta.title}. Fechas: ${trip.meta.dates}. Ciudades: Madrid, Budapest, Praga, Tel Aviv.`}
      />

      <ShareSheet
        open={shareOpen}
        trip={tripMeta}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
